/**
 * Painel mobile do aluno — professor (somente leitura, visão analítica).
 * Não reutiliza a ficha cadastral admin; foco em frequência, saúde, notas e família.
 */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  Heart,
  MessageCircle,
  TrendingDown,
  TrendingUp,
  Minus,
  Users,
  Activity,
  ShieldAlert,
  CheckCircle2,
  Eye,
  BarChart3,
  ClipboardCheck,
  UserX,
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { useFrequenciaAluno } from '../../api/hooks/usePresencasDiarias';
import { useTendenciaAluno } from '../../api/hooks/useTendenciaAluno';
import { usePerfilAcademico, type ClassificacaoRisco } from '../../api/hooks/useObservacoes';
import { useRiscoAluno } from '../../api/hooks/useRiscoEvasao';
import { AvisarResponsavelModal } from './AvisarResponsavelModal';
import { formatDateBR } from '../../utils/dateUtils';
import type { Aluno } from '../../types/models';
import { ROUTES } from '../../api/routes';

type TabKey = 'resumo' | 'saude' | 'notas' | 'familia';

const TABS: { key: TabKey; label: string; icon: typeof Activity }[] = [
  { key: 'resumo', label: 'Resumo', icon: Activity },
  { key: 'saude', label: 'Saúde', icon: Heart },
  { key: 'notas', label: 'Notas', icon: BookOpen },
  { key: 'familia', label: 'Família', icon: Users },
];

const TENDENCIA_CFG = {
  MELHORANDO: { label: 'Melhorando', color: '#16a34a', Icon: TrendingUp },
  ESTAVEL: { label: 'Estável', color: '#2563eb', Icon: Minus },
  PIORANDO: { label: 'Atenção', color: '#dc2626', Icon: TrendingDown },
} as const;

const RISCO_CFG: Record<
  ClassificacaoRisco,
  { label: string; color: string; bg: string; border: string; Icon: typeof CheckCircle2 }
> = {
  BEM: { label: 'Estável', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', Icon: CheckCircle2 },
  ATENCAO: { label: 'Atenção', color: '#d97706', bg: '#fffbeb', border: '#fde68a', Icon: Eye },
  PREOCUPANTE: { label: 'Preocupante', color: '#ea580c', bg: '#fff7ed', border: '#fed7aa', Icon: AlertTriangle },
  CRITICO: { label: 'Crítico', color: '#dc2626', bg: '#fef2f2', border: '#fecaca', Icon: ShieldAlert },
};

const NIVEL_EVASAO_CFG: Record<string, { label: string; color: string; bg: string }> = {
  ATENCAO: { label: 'Atenção', color: '#1e40af', bg: '#eff6ff' },
  ALERTA: { label: 'Alerta', color: '#92400e', bg: '#fef3c7' },
  CRITICO: { label: 'Crítico', color: '#991b1b', bg: '#fef2f2' },
  EVASAO: { label: 'Evasão', color: '#1e1b4b', bg: '#eef2ff' },
};

function getInitials(nome: string) {
  const p = nome.trim().split(/\s+/);
  if (p.length === 1) return p[0][0]?.toUpperCase() || '?';
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function Donut({ pct, color }: { pct: number; color: string }) {
  const dash = `${Math.min(100, Math.max(0, pct)) * 0.975} 100`;
  return (
    <div className="prof-aluno-donut">
      <svg viewBox="0 0 36 36" aria-hidden>
        <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3.5" />
        <circle
          cx="18"
          cy="18"
          r="15.5"
          fill="none"
          stroke={color}
          strokeWidth="3.5"
          strokeDasharray={dash}
          strokeLinecap="round"
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className="prof-aluno-donut-val" style={{ color }}>
        {pct}%
      </span>
    </div>
  );
}

export function MobileProfessorAlunoPainel({
  alunoId,
  variant = 'page',
}: {
  alunoId: string;
  /** page = rota mobile; modal = overlay; panel = master-detail desktop */
  variant?: 'page' | 'modal' | 'panel';
}) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const [tab, setTab] = useState<TabKey>('resumo');
  const [avisoOpen, setAvisoOpen] = useState(false);
  const ano = new Date().getFullYear();

  const { data: aluno, isLoading } = useQuery({
    queryKey: ['aluno', escolaId, alunoId],
    queryFn: async () => {
      const { data } = await api.get(ROUTES.alunos.findOne(escolaId!, alunoId));
      return data as Aluno;
    },
    enabled: !!escolaId && !!alunoId,
  });

  const { frequencia, isLoading: loadingFreq } = useFrequenciaAluno(alunoId);
  const { tendencia, freqAtual, freqAnterior, isLoading: loadingTend } = useTendenciaAluno(
    alunoId,
    aluno?.turmaId,
  );
  const { data: perfil, isLoading: loadingPerfil } = usePerfilAcademico(alunoId);
  const { registro: incidenteEvasao, isLoading: loadingIncidente } = useRiscoAluno(
    alunoId,
    aluno?.turmaId,
  );

  const { data: boletim, isLoading: loadingBoletim } = useQuery({
    queryKey: ['boletim', escolaId, alunoId, ano],
    queryFn: async () => {
      const { data } = await api.get(
        `/escolas/${escolaId}/alunos/${alunoId}/boletim?ano=${ano}`,
      );
      return data as { materias?: Array<{ materiaNome: string; mediaFinal: number | null; bimestres: Array<{ media: number | null }> }> };
    },
    enabled: !!escolaId && !!alunoId && (tab === 'notas' || tab === 'resumo'),
  });

  const pct = frequencia.percentual;
  const semaforoColor = pct >= 85 ? '#16a34a' : pct >= 75 ? '#d97706' : '#dc2626';
  const tendCfg = TENDENCIA_CFG[tendencia];

  const ultimasPresencas = useMemo(() => {
    return [...frequencia.presencas]
      .sort((a, b) => ((b as any).dataReferencia || '').localeCompare((a as any).dataReferencia || ''))
      .slice(0, 10)
      .reverse();
  }, [frequencia.presencas]);

  const alertasSaude = useMemo(() => {
    if (!aluno) return [];
    const items: { label: string; value: string; level: 'warn' | 'info' }[] = [];
    if (aluno.alergias?.trim()) {
      items.push({ label: 'Alergias', value: aluno.alergias, level: 'warn' });
    }
    if (aluno.restricaoAlimentar?.trim()) {
      items.push({ label: 'Restrição alimentar', value: aluno.restricaoAlimentar, level: 'warn' });
    }
    if (aluno.tipoDeficiencia) {
      items.push({
        label: 'NEE',
        value: String(aluno.tipoDeficiencia).replace(/_/g, ' '),
        level: 'info',
      });
    }
    if (aluno.tipoSanguineo) {
      items.push({ label: 'Tipo sanguíneo', value: aluno.tipoSanguineo, level: 'info' });
    }
    return items;
  }, [aluno]);

  const materiasBoletim = boletim?.materias ?? [];
  const mediaGeral = useMemo(() => {
    const vals = materiasBoletim
      .map((m) => m.mediaFinal)
      .filter((v): v is number => v != null);
    if (!vals.length) return null;
    return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 10) / 10;
  }, [materiasBoletim]);

  const analiseRisco = useMemo(() => {
    const fatores: string[] = [];
    const classificacao: ClassificacaoRisco =
      perfil?.risco?.classificacao ??
      (pct < 75 ? 'CRITICO' : pct < 85 ? 'PREOCUPANTE' : tendencia === 'PIORANDO' ? 'ATENCAO' : 'BEM');
    const score = perfil?.risco?.score ?? (pct < 75 ? 45 : pct < 85 ? 28 : 12);

    if (pct < 75) fatores.push('Frequência abaixo de 75% (alerta LDB)');
    else if (pct < 85) fatores.push('Frequência abaixo de 85%');
    if (frequencia.emRisco) fatores.push('Indicador de risco na frequência acumulada');
    if (tendencia === 'PIORANDO') fatores.push('Queda de presença nas últimas 2 semanas');
    if (perfil?.notas?.tendencia === 'DESCENDO') fatores.push('Média escolar em queda');
    if (perfil?.notas?.mediaGeral && perfil.notas.mediaGeral < 6) {
      fatores.push(`Média geral ${perfil.notas.mediaGeral.toFixed(1)} abaixo do esperado`);
    }
    if (perfil?.observacoes?.urgentes) {
      fatores.push(
        `${perfil.observacoes.urgentes} observação${perfil.observacoes.urgentes !== 1 ? 'ões' : ''} urgente${perfil.observacoes.urgentes !== 1 ? 's' : ''}`,
      );
    }
    if (incidenteEvasao?.descricao) {
      fatores.push(incidenteEvasao.descricao);
    } else if (incidenteEvasao?.tipoIncidente) {
      fatores.push(`Incidente: ${incidenteEvasao.tipoIncidente.replace(/_/g, ' ').toLowerCase()}`);
    }

    return { classificacao, score, fatores };
  }, [perfil, pct, frequencia.emRisco, tendencia, incidenteEvasao]);

  const riscoCfg = RISCO_CFG[analiseRisco.classificacao];
  const RiscoIcon = riscoCfg.Icon;

  const responsavelPrincipal = useMemo(() => {
    const list = (aluno as Aluno & { responsaveis?: Array<{ principal?: boolean; responsavel?: { nome: string; telefone?: string; email?: string } }> })
      ?.responsaveis;
    if (!list?.length) return null;
    const pr = list.find((r) => r.principal) || list[0];
    return pr?.responsavel ?? null;
  }, [aluno]);

  if (isLoading || !aluno) {
    return (
      <div className="prof-aluno-painel prof-aluno-painel--loading">
        Carregando painel...
      </div>
    );
  }

  return (
    <div
      className={[
        'prof-aluno-painel',
        variant === 'modal' ? 'prof-aluno-painel--modal' : '',
        variant === 'panel' ? 'prof-aluno-painel--panel' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {/* Hero compacto */}
      <div className="prof-aluno-hero">
        {aluno.fotoUrl ? (
          <img src={aluno.fotoUrl} alt="" className="prof-aluno-hero-photo" />
        ) : (
          <span className="prof-aluno-hero-initials">{getInitials(aluno.nome)}</span>
        )}
        <div className="prof-aluno-hero-text">
          <h2 className="prof-aluno-hero-name">{aluno.nome}</h2>
          <p className="prof-aluno-hero-meta">
            {aluno.matricula}
            {aluno.turma?.nome ? ` · ${aluno.turma.nome}` : ''}
          </p>
        </div>
        <button
          type="button"
          className="prof-aluno-hero-aviso"
          onClick={() => setAvisoOpen(true)}
          title="Avisar responsável"
        >
          <MessageCircle size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div className="prof-aluno-tabs" role="tablist">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`prof-aluno-tab${tab === key ? ' on' : ''}`}
            onClick={() => setTab(key)}
          >
            <Icon size={14} aria-hidden />
            {label}
          </button>
        ))}
      </div>

      {tab === 'resumo' && (
        <div className="prof-aluno-section">
          <article className={`prof-aluno-dash prof-aluno-dash--${analiseRisco.classificacao.toLowerCase()}`}>
            <header className="prof-aluno-dash-head">
              <div className="prof-aluno-dash-head-left">
                <BarChart3 size={17} className="prof-aluno-dash-head-icon" aria-hidden />
                <div>
                  <h3 className="prof-aluno-dash-head-title">Visão analítica</h3>
                  <p className="prof-aluno-dash-head-sub">Indicadores consolidados · {ano}</p>
                </div>
              </div>
              {mediaGeral != null && (
                <button
                  type="button"
                  className="prof-aluno-dash-chip"
                  onClick={() => setTab('notas')}
                >
                  <BookOpen size={13} aria-hidden />
                  {mediaGeral.toFixed(1)}
                </button>
              )}
            </header>

            <div className="prof-aluno-dash-grid">
              <div className="prof-aluno-dash-cell">
                <div className="prof-aluno-dash-cell-icon" style={{ background: `${semaforoColor}18`, color: semaforoColor }}>
                  <Donut pct={pct} color={semaforoColor} />
                </div>
                <div className="prof-aluno-dash-cell-body">
                  <span className="prof-aluno-dash-label">Frequência</span>
                  <span className="prof-aluno-dash-value" style={{ color: semaforoColor }}>
                    {pct}%
                  </span>
                  <span className="prof-aluno-dash-hint">
                    {pct >= 85 ? 'Regular' : pct >= 75 ? 'Atenção' : 'Crítica LDB'}
                  </span>
                </div>
              </div>

              <div className="prof-aluno-dash-cell">
                <div className="prof-aluno-dash-cell-icon prof-aluno-dash-cell-icon--duo">
                  <ClipboardCheck size={18} style={{ color: '#16a34a' }} aria-hidden />
                  <UserX size={16} style={{ color: '#dc2626', marginLeft: -4 }} aria-hidden />
                </div>
                <div className="prof-aluno-dash-cell-body">
                  <span className="prof-aluno-dash-label">Chamadas</span>
                  <div className="prof-aluno-dash-duo-nums">
                    <span style={{ color: '#16a34a' }}>{frequencia.diasPresente}</span>
                    <span className="prof-aluno-dash-duo-sep">/</span>
                    <span style={{ color: '#dc2626' }}>{frequencia.diasAusente}</span>
                  </div>
                  <div className="prof-aluno-dash-stack" aria-hidden>
                    {frequencia.totalDias > 0 ? (
                      <>
                        <span
                          className="prof-aluno-dash-stack-ok"
                          style={{ flex: frequencia.diasPresente || 0.001 }}
                        />
                        <span
                          className="prof-aluno-dash-stack-fail"
                          style={{ flex: frequencia.diasAusente || 0.001 }}
                        />
                      </>
                    ) : (
                      <span className="prof-aluno-dash-stack-empty" />
                    )}
                  </div>
                </div>
              </div>

              <div className="prof-aluno-dash-cell">
                <div className="prof-aluno-dash-cell-icon" style={{ background: `${tendCfg.color}14`, color: tendCfg.color }}>
                  <tendCfg.Icon size={20} aria-hidden />
                </div>
                <div className="prof-aluno-dash-cell-body">
                  <span className="prof-aluno-dash-label">Tendência 14d</span>
                  <span className="prof-aluno-dash-value" style={{ color: tendCfg.color }}>
                    {loadingTend ? '…' : tendCfg.label}
                  </span>
                  <div className="prof-aluno-dash-compare" aria-hidden>
                    <div className="prof-aluno-dash-compare-col">
                      <div
                        className="prof-aluno-dash-compare-bar"
                        style={{ height: `${Math.max(8, freqAnterior)}%`, background: '#94a3b8' }}
                      />
                      <span>Antes</span>
                    </div>
                    <div className="prof-aluno-dash-compare-col">
                      <div
                        className="prof-aluno-dash-compare-bar"
                        style={{ height: `${Math.max(8, freqAtual)}%`, background: tendCfg.color }}
                      />
                      <span>Agora</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="prof-aluno-dash-cell">
                <div className="prof-aluno-dash-cell-icon" style={{ background: riscoCfg.bg, color: riscoCfg.color }}>
                  <RiscoIcon size={20} aria-hidden />
                </div>
                <div className="prof-aluno-dash-cell-body">
                  <span className="prof-aluno-dash-label">Permanência</span>
                  <div className="prof-aluno-dash-value-row">
                    <span className="prof-aluno-dash-value" style={{ color: riscoCfg.color }}>
                      {loadingPerfil ? '…' : riscoCfg.label}
                    </span>
                    <span className="prof-aluno-dash-score" style={{ color: riscoCfg.color }}>
                      {loadingPerfil ? '—' : analiseRisco.score}
                    </span>
                  </div>
                  <div className="prof-aluno-dash-score-track" aria-hidden>
                    <div
                      className="prof-aluno-dash-score-fill"
                      style={{
                        width: `${Math.min(100, analiseRisco.score)}%`,
                        background: riscoCfg.color,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="prof-aluno-dash-insight">
              {loadingPerfil || loadingIncidente ? (
                <p className="prof-aluno-dash-insight-text">Calculando indicadores...</p>
              ) : analiseRisco.classificacao === 'BEM' && !incidenteEvasao ? (
                <p className="prof-aluno-dash-insight-text prof-aluno-dash-insight-text--ok">
                  <CheckCircle2 size={14} aria-hidden />
                  Perfil estável — sem sinais relevantes de evasão.
                </p>
              ) : (
                <ul className="prof-aluno-dash-insight-list">
                  {analiseRisco.fatores.slice(0, 3).map((f) => (
                    <li key={f}>
                      <AlertTriangle size={11} aria-hidden />
                      {f}
                    </li>
                  ))}
                </ul>
              )}
              {incidenteEvasao && (
                <p
                  className="prof-aluno-dash-incidente"
                  style={{
                    color: NIVEL_EVASAO_CFG[incidenteEvasao.nivel]?.color,
                    background: NIVEL_EVASAO_CFG[incidenteEvasao.nivel]?.bg,
                  }}
                >
                  <ShieldAlert size={12} aria-hidden />
                  Motor: {NIVEL_EVASAO_CFG[incidenteEvasao.nivel]?.label ?? incidenteEvasao.nivel}
                </p>
              )}
            </div>

            {alertasSaude.length > 0 && (
              <button
                type="button"
                className="prof-aluno-dash-health"
                onClick={() => setTab('saude')}
              >
                <Heart size={14} aria-hidden />
                {alertasSaude.length} alerta{alertasSaude.length !== 1 ? 's' : ''} de saúde
                <span className="prof-aluno-dash-health-cta">Ver</span>
              </button>
            )}

            <div className="prof-aluno-dash-chart">
              <div className="prof-aluno-dash-chart-head">
                <Activity size={14} aria-hidden />
                <span>Últimas presenças</span>
              </div>
              {loadingFreq ? (
                <p className="prof-aluno-muted">Carregando...</p>
              ) : ultimasPresencas.length === 0 ? (
                <p className="prof-aluno-dash-chart-empty">Sem registros de chamada ainda.</p>
              ) : (
                <div className="prof-aluno-presenca-chart">
                  {ultimasPresencas.map((p) => {
                    const ok = p.status === 'PRESENTE' || p.status === 'ATRASADO';
                    const d = (p as any).dataReferencia
                      ? formatDateBR((p as any).dataReferencia)
                      : '—';
                    return (
                      <div key={p.id} className="prof-aluno-presenca-col" title={d}>
                        <div
                          className={`prof-aluno-presenca-bar${ok ? ' ok' : ' fail'}`}
                          style={{ height: ok ? '100%' : '35%' }}
                        />
                        <span className="prof-aluno-presenca-lbl">{d}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </article>
        </div>
      )}

      {tab === 'saude' && (
        <div className="prof-aluno-section">
          {alertasSaude.length === 0 ? (
            <div className="prof-aluno-card prof-aluno-card--ok">
              <Heart size={20} color="#16a34a" />
              <p>Nenhum alerta de saúde ou NEE registrado.</p>
            </div>
          ) : (
            alertasSaude.map((a) => (
              <div
                key={a.label}
                className={`prof-aluno-alert-card prof-aluno-alert-card--${a.level}`}
              >
                <strong>{a.label}</strong>
                <p>{a.value}</p>
              </div>
            ))
          )}
          {aluno.planoSaude && (
            <div className="prof-aluno-card">
              <span className="prof-aluno-kpi-label">Plano de saúde</span>
              <p className="prof-aluno-card-body">{aluno.planoSaude}</p>
            </div>
          )}
        </div>
      )}

      {tab === 'notas' && (
        <div className="prof-aluno-section">
          {loadingBoletim ? (
            <p className="prof-aluno-muted">Carregando notas...</p>
          ) : materiasBoletim.length === 0 ? (
            <div className="prof-aluno-card">
              <p className="prof-aluno-muted">Nenhuma nota lançada em {ano}.</p>
            </div>
          ) : (
            <>
              {mediaGeral != null && (
                <div className="prof-aluno-card prof-aluno-card--accent">
                  <span className="prof-aluno-card-title">Média geral</span>
                  <span
                    className="prof-aluno-media-geral"
                    style={{ color: mediaGeral >= 6 ? '#16a34a' : '#dc2626' }}
                  >
                    {mediaGeral}
                  </span>
                </div>
              )}
              <div className="prof-aluno-card">
                <h3 className="prof-aluno-card-title">Por disciplina</h3>
                <ul className="prof-aluno-notas-list">
                  {materiasBoletim.map((m) => {
                    const media = m.mediaFinal ?? 0;
                    const width = Math.min(100, (media / 10) * 100);
                    const cor = media >= 6 ? '#16a34a' : '#dc2626';
                    return (
                      <li key={m.materiaNome} className="prof-aluno-nota-row">
                        <div className="prof-aluno-nota-head">
                          <span>{m.materiaNome}</span>
                          <strong style={{ color: cor }}>
                            {m.mediaFinal != null ? m.mediaFinal : '—'}
                          </strong>
                        </div>
                        <div className="prof-aluno-nota-track">
                          <div
                            className="prof-aluno-nota-fill"
                            style={{ width: `${width}%`, background: cor }}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'familia' && (
        <div className="prof-aluno-section">
          {responsavelPrincipal ? (
            <div className="prof-aluno-card">
              <h3 className="prof-aluno-card-title">Responsável principal</h3>
              <p className="prof-aluno-resp-name">{responsavelPrincipal.nome}</p>
              {responsavelPrincipal.telefone && (
                <p className="prof-aluno-card-body">
                  <a href={`tel:${responsavelPrincipal.telefone}`}>
                    {responsavelPrincipal.telefone}
                  </a>
                </p>
              )}
              {responsavelPrincipal.email && (
                <p className="prof-aluno-muted">{responsavelPrincipal.email}</p>
              )}
              <button
                type="button"
                className="prof-aluno-btn-aviso"
                onClick={() => setAvisoOpen(true)}
              >
                <MessageCircle size={16} />
                Avisar responsável
              </button>
            </div>
          ) : (
            <div className="prof-aluno-card">
              <p className="prof-aluno-muted">Nenhum responsável vinculado.</p>
            </div>
          )}
          <p className="prof-aluno-footnote">
            Dados cadastrais completos (CPF, endereço) não são exibidos ao professor por
            política de privacidade.
          </p>
        </div>
      )}

      <AvisarResponsavelModal
        open={avisoOpen}
        onClose={() => setAvisoOpen(false)}
        alunoId={alunoId}
        alunoNome={aluno.nome}
      />
    </div>
  );
}
